import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { Mail, MapPin, Phone, User } from 'lucide-react';

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { SubscriptionDetail } from '@/lib/api/subscriptions';
import { CustomerResourceBadge } from '@/components/customer/CustomerResourceBadge';

interface SubscriptionCustomerTabProps {
  subscription: SubscriptionDetail;
}

export function SubscriptionCustomerTab({ subscription }: SubscriptionCustomerTabProps) {
  const { t } = useTranslation('subscriptions');

  if (!subscription.customer) {
    return <div className="py-8 text-center">{t('customerNotFound')}</div>;
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('customerInformation')}</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 gap-x-6 gap-y-4">
          <div className="space-y-1">
            <p className="text-muted-foreground text-sm font-medium">{t('customerId')}</p>
            <CustomerResourceBadge
              customerId={subscription.customer?.id || ''}
              customerName={subscription.customer?.firstName || ''}
            />
          </div>

          <div className="space-y-1">
            <p className="text-muted-foreground text-sm font-medium">{t('name')}</p>
            <div className="flex items-center">
              <User className="text-muted-foreground mr-2 h-4 w-4" />
              <p>
                {subscription.customer?.firstName || 'N/A'} {subscription.customer?.lastName || 'N/A'}
              </p>
            </div>
          </div>

          <div className="space-y-1">
            <p className="text-muted-foreground text-sm font-medium">{t('email')}</p>
            <div className="flex items-center">
              <Mail className="text-muted-foreground mr-2 h-4 w-4" />
              <p>{subscription.customer?.email || 'N/A'}</p>
            </div>
          </div>

          {subscription.customer?.billingAddress?.phone && (
            <div className="space-y-1">
              <p className="text-muted-foreground text-sm font-medium">{t('phone')}</p>
              <div className="flex items-center">
                <Phone className="text-muted-foreground mr-2 h-4 w-4" />
                <p>{subscription.customer.billingAddress.phone}</p>
              </div>
            </div>
          )}
        </div>

        <Separator className="my-6" />

        <div className="grid grid-cols-1 gap-6">
          {subscription.customer?.billingAddress && (
            <div className="space-y-4">
              <h3 className="flex items-center font-semibold">
                <MapPin className="mr-2 h-4 w-4" />
                {t('billingAddress')}
              </h3>
              <div className="space-y-1">
                <p>
                  {subscription.customer.billingAddress.firstName || 'N/A'}{' '}
                  {subscription.customer.billingAddress.lastName || 'N/A'}
                </p>
                <p>{subscription.customer.billingAddress.address1 || 'N/A'}</p>
                <p>
                  {subscription.customer.billingAddress.city || 'N/A'},{' '}
                  {subscription.customer.billingAddress.state || 'N/A'}{' '}
                  {subscription.customer.billingAddress.postal || 'N/A'}
                </p>
                <p>{subscription.customer.billingAddress.country || 'N/A'}</p>
              </div>
            </div>
          )}

          {subscription.customer?.shippingAddress && (
            <div className="space-y-4">
              <h3 className="flex items-center font-semibold">
                <MapPin className="mr-2 h-4 w-4" />
                {t('shippingAddress')}
              </h3>
              <div className="space-y-1">
                <p>
                  {subscription.customer.shippingAddress.firstName || 'N/A'}{' '}
                  {subscription.customer.shippingAddress.lastName || 'N/A'}
                </p>
                <p>{subscription.customer.shippingAddress.address1 || 'N/A'}</p>
                <p>
                  {subscription.customer.shippingAddress.city || 'N/A'},{' '}
                  {subscription.customer.shippingAddress.state || 'N/A'}{' '}
                  {subscription.customer.shippingAddress.postal || 'N/A'}
                </p>
                <p>{subscription.customer.shippingAddress.country || 'N/A'}</p>
              </div>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
